import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { EventTypes } from '../../../constants/event';
import {
  IMessageReceived,
  messagingActions,
} from '../../../reducers/messagingSlice';
import logger from '../../../utils/logger';
import {
  resetSessionBoundaryData,
  setSessionStart,
} from '../sessionBoundary/sessionBoundary.slice';
import {
  AgentMode,
  DEFAULT_AGENT_MODE,
  SLICE_NAME,
} from './agentMode.constants';

export interface AgentModeState {
  mode: AgentMode;
  /**
   * Whether the agent has actually acted while in `Intercept` — i.e. an
   * `agent_interception` has gone out this session.
   *
   * The mode itself emits nothing (see the reducer below), so until this is true
   * an `Intercept` selection is pure intent: nothing has reached the AI or the
   * POS, and the agent can still switch back. Once it is true the choice is
   * one-way for the rest of the session, because the AI has been told to stop
   * and cannot be handed the microphone back mid-order.
   */
  engaged: boolean;
}

export const initialState: AgentModeState = {
  mode: DEFAULT_AGENT_MODE,
  engaged: false,
};

/**
 * Back to the silent default. Called at every point a *new* HITL session begins
 * — never at session end and never on `car_exit`: a takeover lets the car leave
 * while the same session's order is still being finished, and resetting the
 * control state there is exactly what caused the dropped-order + cart-revert
 * incident (see the reasoning block in CustomerPulse).
 */
const resetToDefault = (state: AgentModeState) => {
  if (state.mode !== DEFAULT_AGENT_MODE || state.engaged) {
    logger.info(`Agent mode reset to ${DEFAULT_AGENT_MODE} on session start`);
  }
  state.mode = DEFAULT_AGENT_MODE;
  state.engaged = false;
};

export const agentModeSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    /**
     * The whole of what pressing the switch does.
     *
     * It emits **nothing**. The mode is permission — it unlocks the agent's
     * surfaces — while the wire events stay where they already are, on the
     * agent's real actions (`agent_interception`, typed by what was done, plus
     * the POS's one-shot `agent_first_interception`).
     *
     * That asymmetry is the point: emitting here would fail *dangerous*, since
     * an accidental press would silence the AI and leave nobody serving the
     * customer. Emitting on the first real action fails *safe* — the AI keeps
     * working until a human actually does something.
     */
    setAgentMode: (state, action: PayloadAction<AgentMode>) => {
      // One-way once engaged: the AI has already stopped, so there is nothing
      // to go back to.
      if (state.engaged && state.mode === AgentMode.intercept) return;
      state.mode = action.payload;
    },
  },
  extraReducers: (builder) => {
    // The agent acted while in Intercept → the takeover is real from here on.
    // Keyed on the same signal the status band reads, so the switch and the band
    // cannot disagree about whether the order has been taken.
    builder.addCase(messagingActions.sendAgentInterception, (state) => {
      if (state.mode === AgentMode.intercept) state.engaged = true;
    });

    // Session start, path 1 of 2 — the task-router flow.
    builder.addCase(setSessionStart, resetToDefault);

    // Restaurant change and the task-router WS teardown; both between orders.
    builder.addCase(resetSessionBoundaryData, resetToDefault);

    // Session start, path 2 of 2 — the single-restaurant flow, where a session
    // begins on car_enter.
    builder.addCase(
      messagingActions.messageReceived,
      (state, action: PayloadAction<IMessageReceived>) => {
        const {
          messages = [],
          isSessionBufferedMessages,
          isCartEmpty,
        } = action.payload;

        // A buffered payload is a REPLAY of past events, so its car_enter is
        // history rather than a new customer and must not reset anything.
        if (isSessionBufferedMessages) return;

        // Only when no order is in flight. If the previous order is still being
        // finished (the car left, the agent is completing it) the cart is not
        // empty, so the agent stays in Intercept — this is the guard that keeps
        // the reset away from the incident above. The cart is the durable
        // signal: `finalOrderSubmitted` is transient and the staff-intervention
        // flags never return to their initial value for the rest of the shift.
        if (!isCartEmpty) return;

        // Deliberately NOT gated on `sessionBoundary.sessionId` being empty the
        // way the session-start reducer is. That guard protects session
        // *identity*; reusing it here would mean a missed car_exit (WS 1006)
        // latches the session id, no session start ever fires, and an agent left
        // in Intercept carries it into the next car. A new car is unambiguously
        // a new customer.
        if (messages.some(({ event }) => event === EventTypes.carEnter)) {
          resetToDefault(state);
        }
      }
    );
  },
});

export const { setAgentMode } = agentModeSlice.actions;

export default agentModeSlice.reducer;
